import * as THREE from "three";
import { Cell, CellKind, Grid } from "@highmarch/domain";
import { Terrain } from "../terrain";
import { OptionBadge } from "./placementMarks";

/** Shapes and badges the placement marks (placement.ts, roadPreview.ts) are drawn with. */

/** The deck height over each chasm cell of `path`: the ground of the bank the crossing leaves from. */
export function deckHeights(path: readonly Cell[], grid: Grid | null): Map<string, number> {
  const out = new Map<string, number>(), T = Terrain.current;
  if (!grid || !T) return out;
  let bank: number | null = null;
  for (const c of path) {
    if (grid.kindAt(c) === CellKind.Chasm) out.set(`${c.x},${c.y}`, bank ?? T.groundOf(c));
    else bank = T.groundOf(c);
  }
  return out;
}

/** A square ring in the XY plane like `quad` (outer and inner half-widths). */
export function squareRing(outer: number, inner: number): THREE.ShapeGeometry {
  const o = outer, i = inner;
  const shape = new THREE.Shape([new THREE.Vector2(-o, -o), new THREE.Vector2(o, -o), new THREE.Vector2(o, o), new THREE.Vector2(-o, o)]);
  shape.holes.push(new THREE.Path([new THREE.Vector2(-i, -i), new THREE.Vector2(-i, i), new THREE.Vector2(i, i), new THREE.Vector2(i, -i)]));
  return new THREE.ShapeGeometry(shape);
}

/** A rounded square's outline (half-width `h`, corner radius `r`) as a path, counter-clockwise or clockwise. */
function roundedPath<T extends THREE.Path>(target: T, h: number, r: number, cw = false): T {
  const k = Math.min(r, h), steps = 6, pts: THREE.Vector2[] = [];
  const corners: [number, number, number][] = [[h - k, -h + k, -Math.PI / 2], [h - k, h - k, 0], [-h + k, h - k, Math.PI / 2], [-h + k, -h + k, Math.PI]];
  for (const [cx, cy, a0] of corners) for (let i = 0; i <= steps; i++) { const a = a0 + (i / steps) * (Math.PI / 2); pts.push(new THREE.Vector2(cx + Math.cos(a) * k, cy + Math.sin(a) * k)); }
  if (cw) pts.reverse();
  target.setFromPoints(pts);
  return target;
}
/** A filled rounded square in the XY plane like `quad` (a build plot's socket). */
export function roundedSquare(half: number, radius: number): THREE.ShapeGeometry {
  return new THREE.ShapeGeometry(roundedPath(new THREE.Shape(), half, radius), 6);
}
/** A rounded square ring (outer and inner half-widths; the inner corners follow the outer ones), like `squareRing`. */
export function roundedRing(outer: number, inner: number, radius: number): THREE.ShapeGeometry {
  const shape = roundedPath(new THREE.Shape(), outer, radius);
  shape.holes.push(roundedPath(new THREE.Path(), inner, Math.max(0.01, radius - (outer - inner)), true));
  return new THREE.ShapeGeometry(shape, 6);
}

/** The sprite material a badge texture is drawn with, one per texture. */
export function badgeMaterial(map: THREE.Texture): THREE.SpriteMaterial {
  let m = badgeMats.get(map);
  if (!m) { m = new THREE.SpriteMaterial({ map, transparent: true, depthTest: false, depthWrite: false, fog: false, toneMapped: false }); badgeMats.set(map, m); }
  return m;
}
const badgeMats = new Map<THREE.Texture, THREE.SpriteMaterial>();

/** The upgrade badge: a gold (chosen) or cream disc with a dark keyline and a dark up-chevron. Cached per state. */
export function upgradeBadgeTexture(chosen: boolean): THREE.Texture | null {
  if (typeof document === "undefined") return null;
  const hit = upgradeBadges.get(chosen);
  if (hit) return hit;
  const S = OptionBadge.TexturePx, cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const g = cv.getContext("2d");
  if (!g) return null;
  g.beginPath(); g.arc(S / 2, S / 2, S / 2 - 2, 0, Math.PI * 2);
  g.fillStyle = "#1c1206"; g.fill();
  g.beginPath(); g.arc(S / 2, S / 2, S / 2 - S * 0.09, 0, Math.PI * 2);
  g.fillStyle = chosen ? "#ffc629" : "#fff1c2"; g.fill();
  // an up-chevron, the "raise a tier" mark
  g.beginPath(); g.moveTo(S * 0.27, S * 0.62); g.lineTo(S * 0.5, S * 0.36); g.lineTo(S * 0.73, S * 0.62);
  g.lineWidth = S * 0.13; g.lineCap = "round"; g.lineJoin = "round"; g.strokeStyle = "#1c1206"; g.stroke();
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  upgradeBadges.set(chosen, t);
  return t;
}
const upgradeBadges = new Map<boolean, THREE.Texture>();
